import { cloneElement, ReactNode, useMemo } from 'react'

import { IconSize, isValidIconElement } from '../../icon/icon'
import { AvatarColor, AvatarColors, AvatarProps, AvatarSize } from '../types'
import { initials } from './initials'
import { sample } from './sample'

export interface UseAvatarResult {
  children: ReactNode
  color: AvatarColor
}

const COLORS_FOR_DRAW: readonly AvatarColor[] = AvatarColors.filter((c) => c !== 'neutral')

const ICON_MAPPING: Record<AvatarSize, IconSize> = {
  sm: 'xs',
  md: 'xs',
  lg: 'xs',
  xl: 'md',
  '2xl': 'lg',
}

type BaseAvatarProps = Pick<AvatarProps, 'children' | 'size' | 'alt' | 'color'>

export function useAvatar<K extends BaseAvatarProps>(props: K): UseAvatarResult {
  return useMemo(() => mapProps(props), [props.children, props.alt, props.size, props.color])
}

function getLetters(props: BaseAvatarProps) {
  const textContent = typeof props.children === 'string' ? props.children : null
  const letters = textContent?.slice(0, 2) || initials(props.alt)
  return letters
}

function mapProps(props: BaseAvatarProps): UseAvatarResult {
  const { children, size } = props

  if (isValidIconElement(children)) {
    const iconSize = size ? ICON_MAPPING[size] : ICON_MAPPING.md
    const icon = cloneElement(children, { size: iconSize })
    const color = props.color ?? 'neutral'
    return { children: icon, color }
  }

  const twoLetters = getLetters(props)
  const letters = twoLetters && props.size === 'sm' ? twoLetters[0] : twoLetters
  const color = props.color ?? sample(twoLetters, COLORS_FOR_DRAW)

  if (children && typeof children !== 'string') {
    return { children, color }
  }

  return {
    children: letters ?? children,
    color,
  }
}
